import type { SearchParty } from '~/apps/search/schemas'
import type { PitchType, CampsiteDetails } from '~/apps/campsite-details/types'

export type ViolationType = 'none' | 'humans' | 'dogs' | 'adults_only'

export interface PartyValidationResult {
  isOverCapacity: boolean
  isAvailable: boolean
  violation: ViolationType
  capacityDetails?: {
    totalPeople: number
    maxPeople: number
    totalDogs: number
    maxDogs: number | null // null = unlimited, 0 = forbidden, >0 = limit
  }
}

/**
 * Validates if a party can fit in a specific pitchtype for undated searches
 * Now supports human, dog, and adults-only validation with specific error categorization
 */
export function validatePitchtypeCapacity(
  party: SearchParty,
  pitchtype: PitchType,
  campsite?: Pick<CampsiteDetails, 'adultsOnly'>,
): PartyValidationResult {
  // Calculate totals
  const totalPeople = party.adults + party.childAges.length
  const totalDogs = party.dogs || 0
  const maxPeople = pitchtype.capacity || 0

  // Handle dog capacity - null means unlimited, 0 means forbidden
  const maxDogs = pitchtype.dogsMax === null ? null : pitchtype.dogsMax || 0

  // Check adults-only violation (highest priority)
  const adultsOnlyViolation = campsite?.adultsOnly && party.childAges.length > 0

  // Check human capacity violation
  const humansExceed = maxPeople > 0 && totalPeople > maxPeople

  // Check dog capacity violation
  // Validate dogs if party has dogs AND:
  // - Dogs are explicitly forbidden (maxDogs === 0), OR
  // - Dogs have a limit and party exceeds it (maxDogs > 0 && totalDogs > maxDogs)
  // Note: maxDogs === null means unlimited dogs (no violation)
  const dogsExceed =
    totalDogs > 0 &&
    (maxDogs === 0 || (maxDogs !== null && maxDogs > 0 && totalDogs > maxDogs))

  // Determine violation type
  // Priority: adults_only > humans > dogs
  let violation: ViolationType = 'none'
  let isOverCapacity = false

  if (adultsOnlyViolation) {
    // Adults-only violation takes highest priority
    violation = 'adults_only'
    isOverCapacity = true
  } else if (humansExceed) {
    // Human capacity violation takes priority over dogs
    violation = 'humans'
    isOverCapacity = true
  } else if (dogsExceed) {
    // Dog capacity violation (only if adults-only and humans don't violate)
    violation = 'dogs'
    isOverCapacity = true
  }

  const isAvailable = !isOverCapacity

  return {
    isOverCapacity,
    isAvailable,
    violation,
    capacityDetails: {
      totalPeople,
      maxPeople,
      totalDogs,
      maxDogs: maxDogs, // null = unlimited, 0 = forbidden, >0 = limit
    },
  }
}

/**
 * Calculates total human count from party data
 */
export function calculateTotalHumans(party: SearchParty): number {
  return party.adults + party.childAges.length
}

/**
 * Checks if party has any restrictions that would prevent booking
 */
export function hasPartyRestrictions(
  party: SearchParty,
  pitchtype: PitchType,
  campsite?: Pick<CampsiteDetails, 'adultsOnly'>,
): boolean {
  const validation = validatePitchtypeCapacity(party, pitchtype, campsite)
  return !validation.isAvailable
}

/**
 * Gets human-readable capacity status for display
 */
export function getCapacityStatus(
  party: SearchParty,
  pitchtype: PitchType,
  campsite?: Pick<CampsiteDetails, 'adultsOnly'>,
):
  | 'available'
  | 'over_capacity'
  | 'humans_exceed'
  | 'dogs_exceed'
  | 'adults_only' {
  const validation = validatePitchtypeCapacity(party, pitchtype, campsite)

  if (validation.isAvailable) {
    return 'available'
  }

  switch (validation.violation) {
    case 'adults_only':
      return 'adults_only'
    case 'humans':
      return 'humans_exceed'
    case 'dogs':
      return 'dogs_exceed'
    default:
      return 'over_capacity'
  }
}

/**
 * Helper functions for checking specific violation types
 */
export function hasAdultsOnlyViolation(
  party: SearchParty,
  pitchtype: PitchType,
  campsite: Pick<CampsiteDetails, 'adultsOnly'>,
): boolean {
  const validation = validatePitchtypeCapacity(party, pitchtype, campsite)
  return validation.violation === 'adults_only'
}

export function hasHumanCapacityViolation(
  party: SearchParty,
  pitchtype: PitchType,
  campsite?: Pick<CampsiteDetails, 'adultsOnly'>,
): boolean {
  const validation = validatePitchtypeCapacity(party, pitchtype, campsite)
  return validation.violation === 'humans'
}

export function hasDogCapacityViolation(
  party: SearchParty,
  pitchtype: PitchType,
  campsite?: Pick<CampsiteDetails, 'adultsOnly'>,
): boolean {
  const validation = validatePitchtypeCapacity(party, pitchtype, campsite)
  return validation.violation === 'dogs'
}
